import { Component, ErrorInfo, ReactNode } from 'react';
import { FLOX_LOGO_BLUE_SRC } from '@constants';
import { captureFrontendError } from '../../observability/capture';
import {
  ExclamationCircleIcon,
  ArrowPathIcon,
  HomeIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
  ArrowTopRightOnSquareIcon,
  CommandLineIcon,
  CpuChipIcon,
  GlobeAltIcon,
  ClockIcon,
  MapPinIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorTimestamp: Date | null;
  copied: boolean;
}

interface EnvironmentInfo {
  userAgent: string;
  platform: string;
  language: string;
  url: string;
  timestamp: string;
  viewport: string;
  memory?: string;
  nodeEnv: string;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorTimestamp: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, errorTimestamp: new Date() };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    captureFrontendError(error, {
      componentStack: errorInfo.componentStack ?? undefined,
      route: window.location.pathname,
    });

    // Log to console for developer visibility
    // eslint-disable-next-line no-console
    console.group('🚨 Error Boundary Caught Error');
    // eslint-disable-next-line no-console
    console.error('Error:', error);
    // eslint-disable-next-line no-console
    console.error('Component Stack:', errorInfo.componentStack);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  // eslint-disable-next-line class-methods-use-this
  getEnvironmentInfo = (): EnvironmentInfo => {
    const nav = navigator as Navigator & { deviceMemory?: number };
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      memory: nav.deviceMemory ? `${nav.deviceMemory}GB` : 'N/A',
      nodeEnv: process.env.NODE_ENV || 'unknown',
    };
  };

  // eslint-disable-next-line class-methods-use-this
  handleReload = (): void => {
    window.location.reload();
  };

  // eslint-disable-next-line class-methods-use-this
  handleGoHome = (): void => {
    window.location.href = '/';
  };

  // eslint-disable-next-line class-methods-use-this
  handleClearAndReload = (): void => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  searchError = (): void => {
    const { error } = this.state;
    if (error) {
      const searchQuery = encodeURIComponent(
        `${error.name}: ${error.message} react`
      );
      window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
    }
  };

  copyFullReport = (): void => {
    const { error, errorInfo, errorTimestamp } = this.state;
    const env = this.getEnvironmentInfo();

    const report = `
═══════════════════════════════════════════════════════════════
                    FLOX ERROR REPORT
═══════════════════════════════════════════════════════════════

TIMESTAMP: ${errorTimestamp?.toISOString() || 'Unknown'}

ERROR
─────────────────────────────────────────────────────────────────
Name: ${error?.name || 'Unknown'}
Message: ${error?.message || 'Unknown'}

STACK TRACE
─────────────────────────────────────────────────────────────────
${error?.stack || 'No stack trace available'}

COMPONENT STACK
─────────────────────────────────────────────────────────────────
${errorInfo?.componentStack || 'No component stack available'}

ENVIRONMENT
─────────────────────────────────────────────────────────────────
URL: ${env.url}
User Agent: ${env.userAgent}
Platform: ${env.platform}
Language: ${env.language}
Viewport: ${env.viewport}
Device Memory: ${env.memory}
Node Env: ${env.nodeEnv}

═══════════════════════════════════════════════════════════════
`;

    navigator.clipboard.writeText(report);
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  // eslint-disable-next-line class-methods-use-this
  parseStackTrace = (
    stack: string
  ): Array<{ fn: string; file: string; line: string }> => {
    const lines = stack.split('\n').slice(1);
    return lines
      .map((line) => {
        const match = line.match(/at\s+(.+?)\s+\((.+):(\d+):\d+\)/);
        if (match) {
          return { fn: match[1], file: match[2], line: match[3] };
        }
        const simpleMatch = line.match(/at\s+(.+):(\d+):\d+/);
        if (simpleMatch) {
          return {
            fn: 'anonymous',
            file: simpleMatch[1],
            line: simpleMatch[2],
          };
        }
        return null;
      })
      .filter(Boolean) as Array<{ fn: string; file: string; line: string }>;
  };

  render(): ReactNode {
    const { hasError, error, errorInfo, errorTimestamp, copied } = this.state;
    const { children } = this.props;

    if (hasError) {
      const env = this.getEnvironmentInfo();
      const parsedStack = error?.stack ? this.parseStackTrace(error.stack) : [];

      return (
        <main className='min-h-screen bg-gray-900 text-gray-100'>
          {/* Header */}
          <header className='bg-red-600 px-6 py-4'>
            <div className='max-w-7xl mx-auto flex items-center justify-between'>
              <div className='flex items-center gap-4'>
                <ExclamationCircleIcon className='h-8 w-8 text-white' />
                <div>
                  <h1 className='text-xl font-bold text-white'>
                    Runtime Error
                  </h1>
                  <p className='text-red-100 text-sm'>
                    {error?.name || 'Error'}:{' '}
                    {error?.message || 'Unknown error'}
                  </p>
                </div>
              </div>
              <img
                src={FLOX_LOGO_BLUE_SRC}
                alt='Flox'
                className='h-8 brightness-0 invert'
              />
            </div>
          </header>

          <div className='max-w-7xl mx-auto px-6 py-8'>
            {/* Quick Actions */}
            <div className='flex flex-wrap gap-3 mb-8'>
              <button
                type='button'
                onClick={this.handleReload}
                className='inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors'
              >
                <ArrowPathIcon className='h-4 w-4' />
                Reload Page
              </button>
              <button
                type='button'
                onClick={this.handleGoHome}
                className='inline-flex items-center gap-2 rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-600 transition-colors'
              >
                <HomeIcon className='h-4 w-4' />
                Go Home
              </button>
              <button
                type='button'
                onClick={this.handleClearAndReload}
                className='inline-flex items-center gap-2 rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-600 transition-colors'
              >
                <CommandLineIcon className='h-4 w-4' />
                Clear Storage & Reload
              </button>
              <button
                type='button'
                onClick={this.searchError}
                className='inline-flex items-center gap-2 rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-600 transition-colors'
              >
                <MagnifyingGlassIcon className='h-4 w-4' />
                Search Error
                <ArrowTopRightOnSquareIcon className='h-3 w-3' />
              </button>
              <button
                type='button'
                onClick={this.copyFullReport}
                className='inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors'
              >
                {copied ? (
                  <>
                    <CheckIcon className='h-4 w-4' />
                    Copied!
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className='h-4 w-4' />
                    Copy Full Report
                  </>
                )}
              </button>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
              {/* Main Error Panel */}
              <div className='lg:col-span-2 space-y-6'>
                {/* Error Message */}
                <section className='bg-gray-800 rounded-xl p-6'>
                  <h2 className='text-lg font-semibold text-white mb-4 flex items-center gap-2'>
                    <ExclamationCircleIcon className='h-5 w-5 text-red-400' />
                    Error Details
                  </h2>
                  <div className='space-y-3'>
                    <div>
                      <span className='text-xs text-gray-400 uppercase tracking-wider'>
                        Type
                      </span>
                      <p className='text-red-400 font-mono text-lg'>
                        {error?.name || 'Error'}
                      </p>
                    </div>
                    <div>
                      <span className='text-xs text-gray-400 uppercase tracking-wider'>
                        Message
                      </span>
                      <p className='text-white font-mono bg-gray-900 rounded-lg p-3 break-all'>
                        {error?.message || 'No message available'}
                      </p>
                    </div>
                  </div>
                </section>

                {/* Stack Trace */}
                {error?.stack && (
                  <section className='bg-gray-800 rounded-xl p-6'>
                    <h2 className='text-lg font-semibold text-white mb-4 flex items-center gap-2'>
                      <CommandLineIcon className='h-5 w-5 text-yellow-400' />
                      Stack Trace
                    </h2>
                    {parsedStack.length > 0 ? (
                      <div className='space-y-1'>
                        {parsedStack.map((frame, index) => (
                          <div
                            key={`${frame.file}-${frame.line}-${index}`}
                            className={`font-mono text-sm p-2 rounded ${
                              index === 0
                                ? 'bg-red-900/30 border border-red-700'
                                : 'bg-gray-900/50'
                            }`}
                          >
                            <span className='text-gray-500 mr-2'>
                              {index + 1}.
                            </span>
                            <span className='text-blue-400'>{frame.fn}</span>
                            <span className='text-gray-500'> at </span>
                            <span className='text-green-400 break-all'>
                              {frame.file}
                            </span>
                            <span className='text-yellow-400'>
                              :{frame.line}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <pre className='text-sm text-gray-300 bg-gray-900 rounded-lg p-4 overflow-x-auto'>
                        {error.stack}
                      </pre>
                    )}
                  </section>
                )}

                {/* Component Stack */}
                {errorInfo?.componentStack && (
                  <section className='bg-gray-800 rounded-xl p-6'>
                    <h2 className='text-lg font-semibold text-white mb-4 flex items-center gap-2'>
                      <CpuChipIcon className='h-5 w-5 text-purple-400' />
                      React Component Stack
                    </h2>
                    <pre className='text-sm text-gray-300 bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-64 overflow-y-auto'>
                      {errorInfo.componentStack}
                    </pre>
                  </section>
                )}
              </div>

              {/* Sidebar */}
              <div className='space-y-6'>
                {/* Timestamp */}
                <section className='bg-gray-800 rounded-xl p-6'>
                  <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2'>
                    <ClockIcon className='h-4 w-4' />
                    Timestamp
                  </h2>
                  <p className='text-white font-mono text-sm'>
                    {errorTimestamp?.toLocaleString()}
                  </p>
                  <p className='text-gray-500 font-mono text-xs mt-1'>
                    {errorTimestamp?.toISOString()}
                  </p>
                </section>

                {/* Environment */}
                <section className='bg-gray-800 rounded-xl p-6'>
                  <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2'>
                    <GlobeAltIcon className='h-4 w-4' />
                    Environment
                  </h2>
                  <dl className='space-y-3 text-sm'>
                    <div>
                      <dt className='text-gray-500'>Node Env</dt>
                      <dd className='text-white font-mono'>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            env.nodeEnv === 'production'
                              ? 'bg-green-900 text-green-300'
                              : 'bg-yellow-900 text-yellow-300'
                          }`}
                        >
                          {env.nodeEnv}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className='text-gray-500'>Viewport</dt>
                      <dd className='text-white font-mono'>{env.viewport}</dd>
                    </div>
                    <div>
                      <dt className='text-gray-500'>Device Memory</dt>
                      <dd className='text-white font-mono'>{env.memory}</dd>
                    </div>
                    <div>
                      <dt className='text-gray-500'>Language</dt>
                      <dd className='text-white font-mono'>{env.language}</dd>
                    </div>
                    <div>
                      <dt className='text-gray-500'>Platform</dt>
                      <dd className='text-white font-mono break-all'>
                        {env.platform}
                      </dd>
                    </div>
                  </dl>
                </section>

                {/* Current URL */}
                <section className='bg-gray-800 rounded-xl p-6'>
                  <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2'>
                    <MapPinIcon className='h-4 w-4' />
                    URL
                  </h2>
                  <p className='text-white font-mono text-sm break-all'>
                    {env.url}
                  </p>
                </section>

                {/* Quick Tips */}
                <section className='bg-gray-800 rounded-xl p-6'>
                  <h2 className='text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3'>
                    💡 Debug Tips
                  </h2>
                  <ul className='text-sm text-gray-300 space-y-2'>
                    <li className='flex items-start gap-2'>
                      <span className='text-gray-500'>•</span>
                      Check the browser console for more details
                    </li>
                    <li className='flex items-start gap-2'>
                      <span className='text-gray-500'>•</span>
                      The first stack frame usually shows the source
                    </li>
                    <li className='flex items-start gap-2'>
                      <span className='text-gray-500'>•</span>
                      Try &quot;Clear Storage & Reload&quot; for state issues
                    </li>
                    <li className='flex items-start gap-2'>
                      <span className='text-gray-500'>•</span>
                      Copy full report for bug tickets
                    </li>
                  </ul>
                </section>
              </div>
            </div>
          </div>
        </main>
      );
    }

    return children;
  }
}
